/**
 * Logger Service
 *
 * Provides centralized semantic logging for the Vue.js application.
 * Logs use semantic format: [topology_id] EVENT_ID: message | data: {...}
 * Logs are only output in development mode to avoid performance overhead
 * and potential information leakage in production.
 */

// Event constants imported for future use in logger instrumentation
// import {
//   LOG_EMIT_START,
//   LOG_EMIT_COMPLETE,
//   LOG_EMIT_FAIL,
// } from "../constants/events.js"

const isDevelopment = process.env.NODE_ENV === "development"
let runtimeLoggingEnabled = true

// LLM:METADATA
// :topology: [Domain:Observability | Role:TopologyManagement | Pattern:Context]
// :relates-to: uses: "logger.js", required-by: "All Components"
// :rationale: "Topology ID injection provides semantic context for all log events"
// :contract: pre: "Component initialized", post: "Topology ID assigned", invariant: "Each component has unique topology ID"
// :decision_cache: "Chose context-based injection over hardcoded strings for flexibility [OBS-002]"
// LLM:END

let currentTopologyId = "UNKNOWN"

/**
 * Set current topology ID for logging context
 * @param {string} topologyId - Component topology ID
 */
export function setTopologyId(topologyId) {
  currentTopologyId = topologyId
}

/**
 * Get current topology ID
 * @returns {string} Current topology ID
 */
export function getTopologyId() {
  return currentTopologyId
}

// LLM:METADATA
// :topology: [Domain:Observability | Role:Logging | Pattern:Service]
// :relates-to: required-by: "All Components"
// :rationale: "Semantic logging enables automated verification and debugging"
// :contract: pre: "Log event triggered", post: "Log formatted and emitted", invariant: "All logs follow semantic format"
// :decision_cache: "Chose semantic format over traditional logging for AI-friendly debugging [OBS-001]"
// LLM:END

/**
 * Log info message with semantic format
 * @param {string} topologyId - Component topology ID
 * @param {string} eventId - SCREAMING_SNAKE_CASE event ID
 * @param {string} message - Human-readable message
 * @param {Object} data - Key application state variables
 */
export function logInfo(topologyId, eventId, message, data = {}) {
  if (!isDevelopment || !runtimeLoggingEnabled) return

  const logEntry = `[${topologyId}] ${eventId}: ${message} | data: ${JSON.stringify(data)}`
  // eslint-disable-next-line no-console
  console.log(logEntry)
}

/**
 * Log warning message with semantic format
 * @param {string} topologyId - Component topology ID
 * @param {string} eventId - SCREAMING_SNAKE_CASE event ID
 * @param {string} message - Human-readable message
 * @param {Object} data - Key application state variables
 */
export function logWarn(topologyId, eventId, message, data = {}) {
  if (!isDevelopment || !runtimeLoggingEnabled) return

  const logEntry = `[${topologyId}] ${eventId}: ${message} | data: ${JSON.stringify(data)}`
  // eslint-disable-next-line no-console
  console.warn(logEntry)
}

/**
 * Log error message with semantic format
 * @param {string} topologyId - Component topology ID
 * @param {string} eventId - SCREAMING_SNAKE_CASE event ID
 * @param {string} message - Human-readable message
 * @param {Object} data - Key application state variables
 */
export function logError(topologyId, eventId, message, data = {}) {
  // CRITICAL: Errors are ALWAYS logged regardless of environment
  // Observability requires error visibility in all environments
  const logEntry = `[${topologyId}] ${eventId}: ${message} | data: ${JSON.stringify(data)}`
  // eslint-disable-next-line no-console
  console.error(logEntry)
}

/**
 * Log debug message with semantic format
 * @param {string} topologyId - Component topology ID
 * @param {string} eventId - SCREAMING_SNAKE_CASE event ID
 * @param {string} message - Human-readable message
 * @param {Object} data - Key application state variables
 */
export function logDebug(topologyId, eventId, message, data = {}) {
  if (!isDevelopment || !runtimeLoggingEnabled) return

  const logEntry = `[${topologyId}] ${eventId}: ${message} | data: ${JSON.stringify(data)}`
  // eslint-disable-next-line no-console
  console.log(logEntry)
}

/**
 * Set runtime verbosity for non-error semantic logs without bypassing environment gating.
 *
 * @precondition enabled is coercible to boolean.
 * @postcondition logInfo/logWarn/logDebug are suppressed when enabled is false.
 * @postcondition Production behavior remains fail-closed for non-error logs regardless of enabled value.
 * @mutates runtimeLoggingEnabled module state.
 * @sideeffects none
 */
export function setEnabled(enabled) {
  runtimeLoggingEnabled = Boolean(enabled)
}

// Export default logger instance with semantic format
// eslint-disable-next-line no-console
export default {
  info: (eventId, message, data) =>
    logInfo(currentTopologyId, eventId, message, data),
  warn: (eventId, message, data) =>
    logWarn(currentTopologyId, eventId, message, data),
  error: (eventId, message, data) =>
    logError(currentTopologyId, eventId, message, data),
  debug: (eventId, message, data) =>
    logDebug(currentTopologyId, eventId, message, data),
  setTopologyId,
  getTopologyId,
  setEnabled,
}
